import { useQuery } from "@tanstack/react-query";
import React from "react";

import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";
import { axiosApi } from "@/Utils";

type ChartViewData = {
  id: string;
  business_unit: string;
  source: string;
  contribution: Record<string, number[]>;
  units: Record<string, number[]>;
};

type TableViewItem = {
  id: string;
  brand: string;
  geography: string;
  source: string;
  contribution: Record<string, number>;
  units: Record<string, number>;
};

export type RelianceRankingResponse = {
  chartView: ChartViewData[];
  tableView: TableViewItem[];
  totals: Record<string, number>;
  saleCategories: string[];
};

// Define the API function for new endpoint
export const getRelianceRankingV2 = async ({
  startDate,
  endDate,
  brand,
  category,
  product,
  geographies,
  businessUnitToggle,
  geoViewToggle,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand: string;
  category: string;
  product: string;
  geographies?: string[];
  businessUnitToggle?: "aggregate" | "split";
  geoViewToggle?: "aggregate" | "split";
}): Promise<RelianceRankingResponse> => {
  const params = new URLSearchParams();

  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  params.append("brand", brand);
  params.append("category", category);
  params.append("product", product);

  if (businessUnitToggle) params.append("businessUnitToggle", businessUnitToggle);
  if (geoViewToggle) params.append("geoViewToggle", geoViewToggle);

  if (geographies?.length) {
    geographies.forEach((geo) => params.append("geographies", geo));
  }

  return axiosApi.get(`/api/v2/financial-review/reliance-ranking/v2`, {
    baseURL: INSIGHTS_BACKEND_URL,
    params: {
      brand,
      category,
      product,
      startDate,
      endDate,
      geographies,
      businessUnitToggle,
      geoViewToggle,
    },
  });
};

// Define the API function for old endpoint
const _getRelianceRankingNest = async ({
  startDate,
  endDate,
  brand,
  category,
  product,
  geographies,
  businessUnitToggle,
  geoViewToggle,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand: string;
  category: string;
  product: string;
  geographies?: string[];
  businessUnitToggle?: "aggregate" | "split";
  geoViewToggle?: "aggregate" | "split";
}): Promise<RelianceRankingResponse> => {
  const params = new URLSearchParams();

  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  params.append("brand", brand);
  params.append("category", category);
  params.append("product", product);

  if (businessUnitToggle) params.append("businessUnitToggle", businessUnitToggle);
  if (geoViewToggle) params.append("geoViewToggle", geoViewToggle);

  if (geographies?.length) {
    geographies.forEach((geo) => params.append("geographies", geo));
  }

  return axiosApi.get(`/api/financial-review/reliance-ranking?${params.toString()}`, {
    baseURL: NEST_BACKEND_URL,
  });
};

export const useRelianceRanking = ({
  startDate,
  endDate,
  brand,
  category,
  product,
  geographies,
  businessUnitToggle = "aggregate",
  geoViewToggle = "aggregate",
  config,
}: {
  startDate?: string | null;
  endDate?: string | null;
  brand: string;
  category: string;
  product: string;
  geographies?: string[];
  businessUnitToggle?: "aggregate" | "split";
  geoViewToggle?: "aggregate" | "split";
  config?: QueryConfig<typeof getRelianceRankingV2 | typeof _getRelianceRankingNest>;
}) => {
  const endpointType = useModuleEndpoint("financial-review");

  const queryKey = React.useMemo(
    () => [
      "reliance-ranking",
      brand,
      category,
      product,
      startDate,
      endDate,
      geographies,
      businessUnitToggle,
      geoViewToggle,
      endpointType,
    ],
    [
      brand,
      category,
      product,
      startDate,
      endDate,
      geographies,
      businessUnitToggle,
      geoViewToggle,
      endpointType,
    ]
  );

  const queryFn = React.useCallback(() => {
    if (endpointType === "new") {
      return _getRelianceRankingNest({
        brand,
        category,
        product,
        startDate,
        endDate,
        geographies,
        businessUnitToggle,
        geoViewToggle,
      });
    } else {
      return getRelianceRankingV2({
        brand,
        category,
        product,
        startDate,
        endDate,
        geographies,
        businessUnitToggle,
        geoViewToggle,
      });
    }
  }, [
    brand,
    category,
    product,
    startDate,
    endDate,
    geographies,
    businessUnitToggle,
    geoViewToggle,
    endpointType,
  ]);

  return useQuery({
    ...config,
    queryKey,
    queryFn,
  });
};
